const ANON_ID_KEY = 'ts_anon_id';

// 30-day attribution window. Chosen deliberately (not the obvious "1 year" default):
//
// 1. Campaign fit — United / email flows complete or drop off within days; 30 days
//    covers "read the email later this month" without year-long first-touch bleed.
// 2. Attribution accuracy — `first_utm_*` on `tracked_identity` is frozen on the
//    first ingest batch for a given anonymous_id. A 365-day cookie means a user who
//    visited www.timeshifter.com organically months ago carries empty UTMs into a
//    later campaign click. Expiring after 30 days lets that returning visitor start
//    a fresh identity with the campaign UTMs correctly captured.
// 3. Persistent-site note — regular visitors are unaffected: the cookie is refreshed
//    on every page load, so it only expires for users absent for 30+ days, which is
//    exactly the cohort where fresh attribution is desirable.
// 4. Safari — ITP already caps JS-set first-party cookies to ~7 days regardless;
//    this constant mainly governs Chrome/Firefox/Edge behavior.
//
// If >7-day persistence on Safari ever becomes critical, revisit server-set
// Set-Cookie on the /tracking/events response (noted in README).
const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60;

function getCookie(name: string): string | null {
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function setCookie(name: string, value: string, domain: string): void {
  const encoded = encodeURIComponent(value);
  const parts = [
    `${name}=${encoded}`,
    `Domain=${domain}`,
    'Path=/',
    `Max-Age=${THIRTY_DAYS_SECONDS}`,
    'SameSite=Lax',
  ];

  // Secure on HTTPS origins; omitted on http:// so local dev cookies work.
  if (location.protocol === 'https:') {
    parts.push('Secure');
  }

  document.cookie = parts.join('; ');
}

export function getOrCreateAnonymousId(cookieDomain: string): string {
  const existing = getCookie(ANON_ID_KEY);
  if (existing) return existing;

  const id = crypto.randomUUID();
  setCookie(ANON_ID_KEY, id, cookieDomain);
  return id;
}

/**
 * If the API echoes back a different anonymousId (server-normalised value),
 * persist it so subsequent calls use the canonical id.
 */
export function reconcileAnonymousId(
  current: string,
  serverId: string,
  cookieDomain: string,
): string {
  if (serverId && serverId !== current) {
    setCookie(ANON_ID_KEY, serverId, cookieDomain);
    return serverId;
  }
  return current;
}
